import { useState } from 'react'
import { createStorageLocation } from '../../services/storageLocationService'
import type { StorageLocation } from '../../types/inventory'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Loader2, Plus } from 'lucide-react'

interface StorageLocationSelectProps {
  value: string | null
  onChange: (storageLocationId: string | null) => void
  storageLocations: StorageLocation[]
  onStorageLocationCreated: (storageLocation: StorageLocation) => void
  disabled?: boolean
  className?: string
}

export function StorageLocationSelect({ value, onChange, storageLocations, onStorageLocationCreated, disabled, className }: StorageLocationSelectProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const displayValue = value ?? '__none__'

  const handleValueChange = (val: string) => {
    if (val === '__new__') {
      setShowCreateModal(true)
      setNewName('')
      setCreateError(null)
    } else {
      onChange(val === '__none__' ? null : val)
    }
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) {
      setCreateError('Name is required')
      return
    }
    setIsCreating(true)
    setCreateError(null)
    try {
      const created = await createStorageLocation({ name })
      onStorageLocationCreated(created)
      onChange(created.id)
      setShowCreateModal(false)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create storage location')
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateModalBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setShowCreateModal(false)
  }

  return (
    <>
      <Select value={displayValue} onValueChange={handleValueChange} disabled={disabled}>
        <SelectTrigger className={`h-8 w-[140px] ${className ?? ''}`}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">—</SelectItem>
          {storageLocations.map((loc) => (
            <SelectItem key={loc.id} value={loc.id}>
              {loc.name}
            </SelectItem>
          ))}
          <SelectItem value="__new__" className="text-blue-600 font-medium">
            <Plus className="h-4 w-4 inline mr-1.5" />
            New...
          </SelectItem>
        </SelectContent>
      </Select>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={handleCreateModalBackdrop}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New storage location</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="storage-location-name" className="text-gray-700">
                  Name
                </Label>
                <Input
                  id="storage-location-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Binder 1"
                  className="mt-1.5"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
              </div>
              {createError && <p className="text-sm text-red-600">{createError}</p>}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={isCreating}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={isCreating || !newName.trim()}>
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Create
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
